import cx from 'classnames';
import PropTypes from 'prop-types';
import { urlUtils } from '../../../utils/url';

import './priceDetails.scss';

const PriceDetails = ({
  hotelInformation,
  labels,
  outBoundInformation,
  priceBreakDown,
  searchInformation,
  showFlightTaxDescription,
  isSummaryView,
}) => {
  const isStopOver = urlUtils.isStopOver();
  const { isDiscounted, specialOffer, totalPrice } = priceBreakDown;

  const joinRoomTypeDescriptions = (rooms, amenities) => {
    const roomDescription = rooms?.map((room) => {
      const roomCountLabel = `(${room.count || room.quantity} ${
        labels.price_roomCountSuffix
      })`;

      return (
        <span
          key={room.description}
          className="iw-price-details-travel-info__room-description"
        >
          {`${room.description} ${roomCountLabel}`}
        </span>
      );
    });

    const amenityList = (
      <span className="iw-price-details-travel-info__room-description">
        {amenities}
      </span>
    );

    return (
      <>
        {roomDescription}
        {amenityList}
      </>
    );
  };

  const renderTravelInfoRow = (
    label,
    value,
    ValueContainerElement = 'span',
  ) => (
    <div className="iw-price-details-travel-info__row">
      <span className="iw-price-details-travel-info__label">{label}</span>
      <ValueContainerElement className="iw-price-details-travel-info__value">
        {value}
      </ValueContainerElement>
    </div>
  );

  const getLabelAndChildAgeLabel = (isAdult, priceLabel, age) => {
    let label = priceLabel;
    let childAgeLabel = '';

    if (isAdult) label += ` ${labels.price_adultLabel}`;
    else {
      label += ` ${labels.price_childLabel}`;
      childAgeLabel = ` (${age} ${labels.price_yearLabel})`;
    }

    return { label, childAgeLabel };
  };

  const renderAmountRow = (
    label,
    quantity,
    pricePerUnit,
    totalPriceValue,
    isDiscountedValue,
    index,
  ) => {
    const rowClasses = cx('iw-price-details-price-table__row', {
      'iw-price-details-price-table__row--discounted': isDiscountedValue,
    });

    const minusSign = isDiscountedValue ? '-' : '';
    const value =
      parseInt(quantity, 10) <= 1
        ? `${totalPriceValue}`
        : `${quantity} x ${minusSign}${pricePerUnit} = ${minusSign}${totalPriceValue}`;

    return (
      <div className={rowClasses} key={index}>
        <div className="iw-price-details-price-table__label">{label}</div>
        {(pricePerUnit || totalPriceValue) && (
          <span className="iw-price-details-price-table__total">{value}</span>
        )}
      </div>
    );
  };

  const renderPassengerPriceGroups = () => {
    const priceGroups = priceBreakDown.passengerPriceGroups;

    return priceGroups.map((priceGroup, index) => {
      const { age, basePrice, basePriceTotal, isAdult, numberOfPassengers } =
        priceGroup;

      const { label, childAgeLabel } = getLabelAndChildAgeLabel(
        isAdult,
        labels.price_basePriceLabel,
        age,
      );

      const labelElement = (
        <span>
          {label}
          {childAgeLabel}
        </span>
      );
      return renderAmountRow(
        labelElement,
        numberOfPassengers,
        basePrice,
        basePriceTotal,
        false,
        index,
      );
    });
  };

  const renderFlightTax = () => {
    const { flightTax } = priceBreakDown;

    if (parseInt(flightTax, 10) <= 0 || flightTax === '') return null;

    const labelElement = (
      <span>
        {labels.price_taxPriceLabel}
        {showFlightTaxDescription ? '*' : ''}
      </span>
    );

    return renderAmountRow(labelElement, 1, flightTax, flightTax, false);
  };

  const renderEuTax = () => {
    const { euTax } = priceBreakDown;

    if (!euTax || parseInt(euTax, 10) <= 0 || euTax === '') return null;

    const labelElement = <span>{labels.price_euTaxLabel}</span>;

    return renderAmountRow(labelElement, 1, euTax, euTax, false);
  };

  const renderTaxAndFees = () => {
    const { taxAndFeesIncludedInPrice } = priceBreakDown;

    if (!taxAndFeesIncludedInPrice || taxAndFeesIncludedInPrice.length === 0)
      return null;

    const rows = taxAndFeesIncludedInPrice?.map((taxAndFee, index) => {
      const labelElement = (
        <span>{`${taxAndFee.description} ${taxAndFee.additionalText}`}</span>
      );

      return renderAmountRow(
        labelElement,
        1,
        taxAndFee.price,
        taxAndFee.price,
        false,
        index,
      );
    });

    return rows;
  };

  const renderPassengerDiscounts = () => {
    const priceGroups = priceBreakDown.passengerPriceGroups;

    return priceGroups.map((priceGroup, index) => {
      const {
        age,
        discount,
        discountTotal,
        hasDiscount,
        isAdult,
        numberOfPassengers,
      } = priceGroup;

      if (!hasDiscount) return null;

      const { label, childAgeLabel } = getLabelAndChildAgeLabel(
        isAdult,
        labels.price_discountLabel,
        age,
      );

      const labelElement = (
        <span>
          {label}
          {childAgeLabel}
        </span>
      );
      return renderAmountRow(
        labelElement,
        numberOfPassengers,
        discount,
        discountTotal,
        true,
        index,
      );
    });
  };

  const renderInternetRebates = () => {
    const { internetRebates } = priceBreakDown;

    return internetRebates?.map((rebate, index) => {
      const labelElement = <span>{rebate.name}</span>;
      return renderAmountRow(labelElement, 1, '', rebate.amount, true, index);
    });
  };

  const renderSpecialOffer = () => {
    if (!specialOffer) return null;

    const labelElement = (
      <span>
        {specialOffer.text}
        {`${
          labels.price_specialOfferLabel
            ? ` ${labels.price_specialOfferLabel}`
            : ''
        }`}
      </span>
    );
    return renderAmountRow(
      labelElement,
      1,
      '',
      specialOffer.formattedDiscount,
      true,
    );
  };

  const renderTotalPriceRow = () => {
    const totalPriceRowClasses = cx('iw-price-details-total-price', {
      'iw-price-details-total-price--discounted': isDiscounted || specialOffer,
    });

    return (
      <div className={totalPriceRowClasses}>
        <span className="iw-price-details-total-price__label">
          {`${labels.price_totalLabel} (${labels.price_totalPriceSuffix})`}
        </span>
        <span className="iw-price-details-total-price__value">
          {totalPrice}
        </span>
      </div>
    );
  };

  const renderTaxAndFeesTitle = () => {
    const { taxAndFeesPayAtDestination } = priceBreakDown;

    if (
      !taxAndFeesPayAtDestination ||
      taxAndFeesPayAtDestination?.taxAndFees?.length <= 0
    )
      return null;

    const labelElement = (
      <span className="iw-price-details-tax-price__title">
        {taxAndFeesPayAtDestination.title}:
      </span>
    );

    return renderAmountRow(labelElement, 1, null, null, false, 0);
  };

  const renderTaxAndFeesPayAtDestination = () => {
    const { taxAndFeesPayAtDestination } = priceBreakDown;

    if (
      !taxAndFeesPayAtDestination ||
      taxAndFeesPayAtDestination?.taxAndFees?.length <= 0
    )
      return null;

    return taxAndFeesPayAtDestination?.taxAndFees?.map((taxAndFee, index) => {
      const labelElement = <span>{taxAndFee.description}</span>;

      return renderAmountRow(
        labelElement,
        1,
        taxAndFee.price,
        taxAndFee.price,
        false,
        index,
      );
    });
  };

  const renderTaxPriceRow = () => (
    <div className="iw-price-details-tax-price">
      {renderTaxAndFeesTitle()}
      {renderTaxAndFeesPayAtDestination()}
    </div>
  );

  const renderFlightTaxDescription = () => {
    if (!showFlightTaxDescription) return false;

    return (
      <div className="iw-price-details__flight-tax-description">
        *
        <span className="iw-price-details__flight-tax-description-text">
          {labels.price_taxDisclaimer}
        </span>
      </div>
    );
  };

  const departureValue = `${outBoundInformation?.departureDate}, ${labels.price_departureTimePrefix} ${outBoundInformation?.departureTime}`;
  const priceDetailsTravelInfo = (
    <div className="iw-price-details-travel-info">
      {hotelInformation &&
        renderTravelInfoRow(labels.price_hotelLabel, hotelInformation.name)}
      {renderTravelInfoRow(
        labels.price_passengerLabel,
        searchInformation.passengerInfo,
      )}
      {hotelInformation &&
        renderTravelInfoRow(
          labels.price_roomTypeLabel,
          joinRoomTypeDescriptions(
            hotelInformation.roomDescription,
            hotelInformation.amenitiesDescription,
          ),
          'div',
        )}
      {outBoundInformation &&
        renderTravelInfoRow(
          labels.price_departureAirportLabel,
          outBoundInformation.departureLocation,
        )}
      {outBoundInformation &&
        renderTravelInfoRow(labels.price_departureLabel, departureValue)}
      {renderTravelInfoRow(
        labels.price_tripDurationLabel,
        searchInformation.durationInfo,
      )}
    </div>
  );
  return (
    <div className="iw-price-details">
      {isSummaryView || !isStopOver ? priceDetailsTravelInfo : null}
      <div className="iw-price-details-price-table">
        {renderPassengerPriceGroups()}
        {renderFlightTax()}
        {renderEuTax()}
        {renderTaxAndFees()}
        {renderPassengerDiscounts()}
        {renderInternetRebates()}
        {renderSpecialOffer()}
      </div>
      <div className="iw-price-details-divider" />
      {renderTotalPriceRow()}
      {renderTaxPriceRow()}
      {renderFlightTaxDescription()}
    </div>
  );
};

PriceDetails.propTypes = {
  hotelInformation: PropTypes.shape({
    amenitiesDescription: PropTypes.string,
    hotelName: PropTypes.string,
    name: PropTypes.string,
    roomDescription: PropTypes.shape({
      count: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
    roomType: PropTypes.string,
  }),
  labels: PropTypes.shape({
    price_adultLabel: PropTypes.string,
    price_basePriceLabel: PropTypes.string,
    price_childLabel: PropTypes.string,
    price_departureAirportLabel: PropTypes.string,
    price_departureLabel: PropTypes.string,
    price_departureTimePrefix: PropTypes.string,
    price_discountLabel: PropTypes.string,
    price_euTaxLabel: PropTypes.string,
    price_hotelLabel: PropTypes.string,
    price_passengerLabel: PropTypes.string,
    price_roomCountSuffix: PropTypes.string,
    price_roomTypeLabel: PropTypes.string,
    price_specialOfferLabel: PropTypes.string,
    price_taxDisclaimer: PropTypes.string,
    price_taxPriceLabel: PropTypes.string,
    price_totalLabel: PropTypes.string,
    price_totalPriceSuffix: PropTypes.string,
    price_tripDurationLabel: PropTypes.string,
    price_yearLabel: PropTypes.string,
  }).isRequired,
  outBoundInformation: PropTypes.shape({
    departureDate: PropTypes.string,
    departureLocation: PropTypes.string,
    departureTime: PropTypes.string,
  }),
  priceBreakDown: PropTypes.shape({
    euTax: PropTypes.string,
    flightTax: PropTypes.string,
    internetRebates: PropTypes.arrayOf(PropTypes.shape({})),
    isDiscounted: PropTypes.bool.isRequired,
    passengerPriceGroups: PropTypes.arrayOf(PropTypes.shape({})),
    specialOffer: PropTypes.shape({
      text: PropTypes.string,
      formattedDiscount: PropTypes.string,
    }),
    taxAndFeesIncludedInPrice: PropTypes.arrayOf(PropTypes.shape({})),
    taxAndFeesPayAtDestination: PropTypes.shape({
      taxAndFees: PropTypes.arrayOf(PropTypes.shape({})),
      title: PropTypes.string,
    }),
    totalPrice: PropTypes.string.isRequired,
  }).isRequired,
  searchInformation: PropTypes.shape({
    durationInfo: PropTypes.string.isRequired,
    passengerInfo: PropTypes.string.isRequired,
  }).isRequired,
  showFlightTaxDescription: PropTypes.bool,
  isSummaryView: PropTypes.bool,
};

PriceDetails.defaultProps = {
  hotelInformation: null,
  outBoundInformation: null,
  showFlightTaxDescription: false,
  isSummaryView: false,
};

export default PriceDetails;
